import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { getFacturasDuplicadas, deleteFactura } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import toast from 'react-hot-toast';

function formatCurrency(value) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
}

export default function Duplicados() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getFacturasDuplicadas();
      // Group by hash
      const byHash = {};
      (Array.isArray(data) ? data : []).forEach((f) => {
        const h = f.hash_archivo;
        if (!byHash[h]) byHash[h] = [];
        byHash[h].push(f);
      });
      setGroups(Object.values(byHash));
    } catch (err) {
      toast.error('Error cargando duplicados: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta factura duplicada?')) return;
    try {
      await deleteFactura(id);
      toast.success('Factura eliminada');
      loadData();
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Facturas Duplicadas</h1>
        {groups.length > 0 && (
          <span className="text-sm text-gray-500">
            {groups.length} grupo{groups.length !== 1 ? 's' : ''} de duplicados
          </span>
        )}
      </div>

      {groups.length === 0 ? (
        <div className="card text-center py-12">
          <AlertTriangle className="w-10 h-10 text-green-400 mx-auto mb-3" />
          <p className="text-gray-500">No se encontraron facturas duplicadas</p>
        </div>
      ) : (
        groups.map((group, gi) => (
          <div key={gi} className="card !p-4">
            <div className="flex items-center gap-2 mb-3">
              <Copy className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-medium text-gray-700">
                {group.length} copias — {group[0]?.archivo_original || 'archivo'}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-2 text-left text-xs text-gray-500">ID</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Fecha</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Proveedor</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Estado</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500">Total</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Origen</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Importada</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {group.map((f) => (
                    <tr key={f.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-600">#{f.id}</td>
                      <td className="px-3 py-2">{f.fecha_factura || '-'}</td>
                      <td className="px-3 py-2">{f.proveedor_nombre || '-'}</td>
                      <td className="px-3 py-2"><StatusBadge status={f.estado} /></td>
                      <td className="px-3 py-2 text-right font-mono">{formatCurrency(f.total)}</td>
                      <td className="px-3 py-2 text-gray-500">{f.origen}</td>
                      <td className="px-3 py-2 text-xs text-gray-400">
                        {f.created_at ? new Date(f.created_at).toLocaleDateString('es-ES') : '-'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <button
                            onClick={() => navigate(`/facturas/${f.id}`)}
                            className="text-xs text-primary-600 hover:underline"
                          >
                            Ver
                          </button>
                          <button
                            onClick={() => handleDelete(f.id)}
                            className="text-xs text-red-500 hover:text-red-700 ml-2"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
